package com.swetdot.swetgram.ui.profile

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Switch
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import com.swetdot.swetgram.domain.NotificationPreferences
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetCard
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetLoading
import com.swetdot.swetgram.ui.design.SwetTopAppBar
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.notifications_discover
import swetgram.shared.generated.resources.notifications_discover_preview
import swetgram.shared.generated.resources.notifications_groups
import swetgram.shared.generated.resources.notifications_mentions
import swetgram.shared.generated.resources.notifications_message_preview
import swetgram.shared.generated.resources.notifications_messages
import swetgram.shared.generated.resources.notifications_retry
import swetgram.shared.generated.resources.notifications_security
import swetgram.shared.generated.resources.notifications_security_note
import swetgram.shared.generated.resources.notifications_streaks
import swetgram.shared.generated.resources.notifications_title
import swetgram.shared.generated.resources.notifications_unavailable

@Composable
fun NotificationSettingsScreen(
    state: NotificationSettingsUiState,
    onChange: (NotificationPreferences) -> Unit,
    onBack: () -> Unit,
    onRetry: () -> Unit,
    onDismissError: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier.fillMaxSize()) {
        SwetTopAppBar(title = stringResource(Res.string.notifications_title), onBack = onBack)
        state.error?.let {
            SwetErrorBanner(message = it, onRetry = onRetry, onDismiss = onDismissError)
        }
        val settings = state.preferences
        when {
            settings == null && !state.loadedOnce -> SwetLoading()
            settings == null -> SwetEmptyState(
                title = stringResource(Res.string.notifications_unavailable),
                body = stringResource(Res.string.notifications_unavailable),
                actionText = stringResource(Res.string.notifications_retry),
                onAction = onRetry,
            )
            else -> Column(
                Modifier.fillMaxSize().navigationBarsPadding().verticalScroll(rememberScrollState())
                    .padding(horizontal = Space.screenMargin),
            ) {
                Spacer(Modifier.height(Space.two))
                SwetCard(contentPadding = PaddingValues(vertical = Space.half)) {
                    ToggleRow(stringResource(Res.string.notifications_messages), settings.messages, state.saving) {
                        onChange(settings.copy(messages = it))
                    }
                    ToggleRow(stringResource(Res.string.notifications_groups), settings.groups, state.saving) {
                        onChange(settings.copy(groups = it))
                    }
                    ToggleRow(stringResource(Res.string.notifications_discover), settings.discover, state.saving) {
                        onChange(settings.copy(discover = it))
                    }
                    ToggleRow(stringResource(Res.string.notifications_mentions), settings.mentions, state.saving) {
                        onChange(settings.copy(mentions = it))
                    }
                    ToggleRow(stringResource(Res.string.notifications_streaks), settings.streaks, state.saving) {
                        onChange(settings.copy(streaks = it))
                    }
                }
                Spacer(Modifier.height(Space.two))
                SwetCard(contentPadding = PaddingValues(vertical = Space.half)) {
                    ToggleRow(
                        stringResource(Res.string.notifications_message_preview),
                        settings.showMessagePreview,
                        state.saving,
                    ) { onChange(settings.copy(showMessagePreview = it)) }
                    ToggleRow(
                        stringResource(Res.string.notifications_discover_preview),
                        settings.showDiscoverCommentPreview,
                        state.saving,
                    ) { onChange(settings.copy(showDiscoverCommentPreview = it)) }
                    ProfileControlRow(
                        title = stringResource(Res.string.notifications_security),
                        supporting = stringResource(Res.string.notifications_security_note),
                    ) { Switch(checked = true, onCheckedChange = null, enabled = false) }
                }
                Spacer(Modifier.height(Space.six))
            }
        }
    }
}

@Composable
private fun ToggleRow(title: String, checked: Boolean, busy: Boolean, onChange: (Boolean) -> Unit) {
    ProfileControlRow(title = title) {
        Switch(checked = checked, onCheckedChange = onChange, enabled = !busy)
    }
}
